import { useContext, useCallback, useEffect, useMemo, useState } from "react";
import moment from "moment";
import classnames from "classnames";
import { Flex, Image } from "uikit-react";
import { useCookies } from "react-cookie";
import UIkit from "uikit";

import TableLoading from "../common/Loading/table_loading";
import { Link } from "../../ui";
import { AppContexts } from "../../providers";
import { WorkSchedulesContext } from "../../providers/work_schedules.provider";
import Pagination from "../common/Pagination";
import RecordsPerPage from "../common/RecordsPerPage";
import { DeleteDialog } from "../common/Operations";

import WorkScheduleEditor from "./work_schedule_editor";
import WorkSchedulesService from "../../services/work_schedules.service";
import { usePermissionsStore } from "../permissions/permissions_store";

const makeTimeOptions = (stepMins = 15) => {
  const opts = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += stepMins) {
      opts.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);}
  }
  return opts;
};

const timeOptionsStart = makeTimeOptions(15);
const timeOptionsEnd = [...makeTimeOptions(15), "23:59"];

// Filter end times to only show times greater than start time
const getFilteredEndTimes = (startTime) => {
  if (!startTime) return timeOptionsEnd;
  const startMinutes = moment(startTime, "HH:mm").hours() * 60 + moment(startTime, "HH:mm").minutes();
  return timeOptionsEnd.filter(t => {
    const endMinutes = moment(t, "HH:mm").hours() * 60 + moment(t, "HH:mm").minutes();
    return endMinutes > startMinutes;
  });
};

const hhmmToLabel = (hhmm) => moment(hhmm, "HH:mm").format("h:mma");

const WorkSchedules = () => {
  const { canEdit } = usePermissionsStore();
  const [criteria, setCriteria] = useState({
    page: 0,
    search_term: "",
    sort: "updated_at",
  });

  const inboxCtx = useContext(AppContexts.InboxContext);
  const workSchedulesCtx = useContext(WorkSchedulesContext);

  const [inbox, setInbox] = useState(null);

  const [exceptionsUser, setExceptionsUser] = useState(null);
  const [exceptions, setExceptions] = useState([]); // list for selected user
  const [editingException, setEditingException] = useState(null);
  const [exceptionsView, setExceptionsView] = useState("list"); // "list" | "form"

  const [form, setForm] = useState({
    exception_date: "",
    exception_type: "off_all_day",
    start_time: "",
    end_time: "",
    reason: "",
    note: "",
  });

  const [formErrors, setFormErrors] = useState({});

  // Reactive client-side validation for the exception form
  const exceptionDateError = useMemo(() => {
    if (!form.exception_date) return null;
    const today = moment().startOf("day");
    if (moment(form.exception_date, "YYYY-MM-DD", true).isBefore(today)) {
      return "Cannot create exceptions for past dates.";
    }
    return null;
  }, [form.exception_date]);

  const [cookies] = useCookies(["records-per-page"]);
  const { "records-per-page": recordsPerPage = 20 } = cookies;

  // ----- Work Schedule Editor state -----
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorUser, setEditorUser] = useState(null);

  // ----- Delete dialog state -----
  const [deleteRecipient, setDeleteRecipient] = useState(null);
  const [deleteException, setDeleteException] = useState(null);
  const canManageWorkSchedules = canEdit("operations.work_schedules");

  const resetForm = () => {
    setEditingException(null);
    setForm({
      exception_date: "",
      exception_type: "off_all_day",
      start_time: "",
      end_time: "",
      reason: "",
      note: "",
    });
    setFormErrors({});
  };

  const EXCEPTION_TYPE_LABELS = {
    off_all_day: "Day Off",
    off_partial: "Partial Day Off",
    working_override: "Working",
  };

  // Fetch work schedules for a recipient from API
  const fetchWorkSchedule = useCallback(async (recipient_id) => {
    try {
      const response = await WorkSchedulesService.getSchedules(recipient_id);
      return response.data || [];
    } catch (error) {
      console.error("Error fetching work schedule:", error);
      return [];
    }
  }, []);

  // Fetch exceptions for a recipient from API
  const fetchExceptions = useCallback(async (recipient_id) => {
    try {
      const response = await WorkSchedulesService.getExceptionsByRecipient(recipient_id);
      return response.data || [];
    } catch (error) {
      console.error("Error fetching exceptions:", error);
      return [];
    }
  }, []);

  const openExceptionsCanvas = useCallback(
    async (recipient) => {
      setExceptionsUser(recipient);
      setExceptionsView("list");
      resetForm();

      // Fetch exceptions from API
      const fetchedExceptions = await fetchExceptions(recipient.recipient_id);
      setExceptions(fetchedExceptions);

      setTimeout(() => {
        try {
          UIkit.offcanvas("#offcanvas-schedule-exceptions")?.show?.();
        } catch (e) {}
      }, 0);
    },
    [fetchExceptions],
  );

  const closeExceptionsCanvas = useCallback(() => {
    try {
      UIkit.offcanvas("#offcanvas-schedule-exceptions")?.hide?.();
    } catch (e) {}

    setExceptionsUser(null);
    setExceptions([]);
    setExceptionsView("list");
    resetForm();
  }, []);

  // Get schedule data for a recipient
  const getRecipientSchedule = useCallback((recipient_id) => {
    const recipient = workSchedulesCtx.recipients.find(r => r.recipient_id === recipient_id);
    return recipient?.schedule || { raw: [], summary: [] };
  }, [workSchedulesCtx.recipients]);

  // Get future exceptions for a recipient
  const getRecipientExceptions = useCallback((recipient_id) => {
    const recipient = workSchedulesCtx.recipients.find(r => r.recipient_id === recipient_id);
    return recipient?.future_exceptions || [];
  }, [workSchedulesCtx.recipients]);

  useEffect(() => {
    document.title = "Work Schedules - RueBaRue";
    if (!!inboxCtx.settings) setInbox(inboxCtx.settings);
  }, [inboxCtx.settings, inboxCtx.loading]);

  useEffect(() => {
    inboxCtx.get();
  }, []);

  // Load recipients with schedules when criteria changes
  useEffect(() => {
    if (inbox?.recipients) {
      const params = {
        page: criteria.page,
        per_page: recordsPerPage,
        sort: criteria.sort || 'updated_at'
      };
      
      if (criteria.search_term && criteria.search_term.trim()) {
        params.search_term = criteria.search_term.trim();
      }
      
      workSchedulesCtx.getRecipientsWithSchedules(params);
    }
  }, [inbox, criteria.page, criteria.search_term, criteria.sort, recordsPerPage]);

  useEffect(() => {
    const el = document.getElementById("offcanvas-schedule-exceptions");
    if (!el) return;

    const onHidden = () => {
      // ensure state is cleaned when user closes via "X" or overlay
      setExceptionsUser(null);
      setExceptions([]);
      setExceptionsView("list");
      resetForm();
    };

    el.addEventListener("hidden", onHidden);
    return () => el.removeEventListener("hidden", onHidden);
  }, []);

  const handleIndexChanged = useCallback((newPageIndex) => {
    setCriteria((c) => ({ ...c, page: newPageIndex }));
  }, []);

  const handleCriteriaChange = useCallback((key, value) => {
    setCriteria((c) => ({
      ...c,
      [key]: value,
      // Reset to page 0 when search or sort changes
      ...(key === 'search_term' || key === 'sort' ? { page: 0 } : {})
    }));
  }, []);

  const handleEditSchedule = async (recipient) => {
    const scheduleData = getRecipientSchedule(recipient.recipient_id);

    setEditorUser({
      ...recipient,
      work_schedule: scheduleData.raw,
    });

    setEditorOpen(true);

    // show offcanvas (optional; WorkScheduleEditor also renders when open=true)
    setTimeout(() => {
      try {
        UIkit.offcanvas("#work-schedule-editor")?.show?.();
      } catch (e) {}
    }, 0);
  };

  const handleDeleteSchedule = (recipient) => {
    setDeleteRecipient(recipient);
    UIkit.modal("#modal-work-schedule-delete").show();
  };

  const confirmDeleteSchedule = async (recipient) => {
    try {
      await workSchedulesCtx.deleteSchedules(recipient.recipient_id);
      UIkit.modal("#modal-work-schedule-delete").hide();
      setDeleteRecipient(null);
    } catch (error) {
      console.error("Error deleting schedule:", error);
    }
  };

  const confirmDeleteException = async (exception) => {
    try {
      await workSchedulesCtx.deleteException(exception.id);
      setExceptions((prev) => prev.filter((x) => x.id !== exception.id));
      setDeleteException(null);
    } catch (error) {
      console.error("Error deleting exception:", error);
    }
  };

  const handleEditorSave = async ({ schedule, holiday_overrides }) => {
    const recipient_id = editorUser?.recipient_id;

    if (!recipient_id) {
      UIkit.notification("No recipient selected.", "danger");
      return;
    }

    const scheduleData = getRecipientSchedule(recipient_id);

    if (scheduleData.raw && scheduleData.raw.length > 0) {
      await workSchedulesCtx.updateSchedules(recipient_id, schedule);
    } else {
      await workSchedulesCtx.createSchedules(recipient_id, schedule);
    }

    // Close editor — WorkScheduleEditor also calls onClose after onSave resolves,
    // but we set state here too to ensure cleanup
    setEditorOpen(false);
    setEditorUser(null);
  };

  return (
    <>
      <section id="work-schedules-template">
        <div id="hms-page-title">
          <h1 className="uk-heading-small">Work Schedules</h1>
        </div>

        <div id="hms-main-body">
          <div className="uk-card uk-card-small uk-card-default hms-form-card card-without-filters">
            <div className="uk-overflow-auto">
              <Flex alignment="between top" className="guest-form-top-filters">
                <div>
                  <form action="" className="uk-form uk-flex uk-flex-middle hms-form">
                    <div className="uk-inline">
                      <div className="uk-search uk-search-default">
                        {criteria.search_term ? (
                          <a
                            className="uk-form-icon-flip uk-form-icon"
                            uk-icon="icon: close"
                            onClick={() => handleCriteriaChange("search_term", "")}
                          ></a>
                        ) : (
                          <span className="uk-search-icon-flip" uk-search-icon="true" />
                        )}
                        <input
                          className="uk-search-input"
                          type="search"
                          placeholder="Name"
                          value={criteria.search_term}
                          onChange={(e) => handleCriteriaChange("search_term", e.target.value)}
                        />
                      </div>
                    </div>
                  </form>
                </div>

                <div className="uk-flex uk-flex-middle" style={{ gap: 10 }}>
                  <div className="hms-sortby-btn">
                    <button className="uk-button" type="button">
                      SORT BY<span uk-icon="icon: triangle-down"></span>
                    </button>
                    <div uk-dropdown="mode: click; offset: 0; animation: uk-animation-slide-top-small; duration: 400">
                      <ul className="uk-nav uk-dropdown-nav">
                        <li className={classnames({ "uk-active": criteria.sort === "updated_at" })}>
                          <Link href="#" onClick={() => handleCriteriaChange("sort", "updated_at")}>
                            Last Updated
                          </Link>
                        </li>
                        <li className={classnames({ "uk-active": criteria.sort === "first_name" })}>
                          <Link href="#" onClick={() => handleCriteriaChange("sort", "first_name")}>
                            First Name
                          </Link>
                        </li>
                        <li className={classnames({ "uk-active": criteria.sort === "last_name" })}>
                          <Link href="#" onClick={() => handleCriteriaChange("sort", "last_name")}>
                            Last Name
                          </Link>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </Flex>

              <table className="uk-table uk-table-hover uk-table-divider hms-table">
                <thead>
                  <tr>
                    <th className="uk-table-expand">User</th>
                    <th className="uk-table-expand">Standard Work Schedule</th>
                    <th className="uk-table-small">Upcoming Availability Exceptions</th>
                    <th className="uk-width-small">Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {inboxCtx.loading || !inbox || workSchedulesCtx.loading ? (
                    TableLoading(6)
                  ) : (
                  workSchedulesCtx.recipients.map((recipient) => {
                    const scheduleData = getRecipientSchedule(recipient.recipient_id);
                    const scheduleSummary = scheduleData.summary || [];
                    const futureExceptions = getRecipientExceptions(recipient.recipient_id);

                    return (
                      <tr key={recipient.recipient_id || recipient.email}>
                        <td>
                          <div className="semi-bold-font">{recipient.name || recipient.email || "—"}</div>
                          <div className="secondary">{recipient.email}</div>

                          {!!recipient.primary_recipient && (
                            <div className="uk-margin-small-top">
                              <span className="hms-booking-tag">Admin</span>
                            </div>
                          )}
                        </td>

                        <td>
                          <div className="schedule-summary">
                            {scheduleSummary.length > 0 ? (
                              scheduleSummary.map((line, idx) => (
                                <div key={idx} className="schedule-line">
                                  {line}
                                </div>
                              ))
                            ) : (
                              <span className="uk-text-muted">No schedule set</span>
                            )}
                          </div>
                        </td>

                        <td>
                          {futureExceptions.length > 0 ? (
                            <div>
                              {futureExceptions.map((exc, idx) => {
                                const isPartialOff = exc.exception_type === "off_partial";

                                const st = exc.start_time
                                  ? moment(String(exc.start_time).slice(0, 5), "HH:mm").format("h:mma")
                                  : null;

                                const et = exc.end_time
                                  ? moment(String(exc.end_time).slice(0, 5), "HH:mm").format("h:mma")
                                  : null;

                                return (
                                  <div key={idx}>
                                    {moment.utc(exc.exception_date).format("MMM DD, YYYY, ddd")}
                                    {" - "}
                                     <span className="uk-text-meta">{EXCEPTION_TYPE_LABELS[exc.exception_type] || exc.exception_type}</span>

                                    {isPartialOff && st && et && (
                                      <span className="uk-text-meta">{" • "}{st} - {et}</span>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <span className="uk-text-muted">—</span>
                          )}
                        </td>

                        <td className="hms-action-btn">               
                          {canManageWorkSchedules && (
                            <div className="uk-flex">
                              <button
                                className="uk-button"
                                type="button"
                                onClick={() => handleEditSchedule(recipient)}
                                uk-tooltip="Edit Schedule"
                              >
                                <Image src="/images/edit.svg" />
                              </button>

                              <button
                                className="uk-button"
                                type="button"
                                onClick={() => openExceptionsCanvas(recipient)}
                                uk-tooltip="Manage Exceptions"
                              >
                                <Image src="/images/work-exception.svg" />
                              </button>

                              <button
                                className="uk-button"
                                uk-toggle="target: #modal-work-schedule-delete"
                                onClick={() => handleDeleteSchedule(recipient)}
                                uk-tooltip="Clear Schedule"
                              >
                                <Image src="/images/delete.svg" />
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
                </tbody>
              </table>
            </div>
          </div>

          <Flex alignment="between top" className="guest-form-top-filters">
            <Pagination
              count={workSchedulesCtx.recipientsCount}
              perPage={recordsPerPage}
              index={criteria.page}
              onIndexChanged={handleIndexChanged}
            />
            {workSchedulesCtx.recipientsCount > 0 && <RecordsPerPage />}
          </Flex>
        </div>
      </section>

      <WorkScheduleEditor
        open={editorOpen}
        onClose={() => {
          setEditorOpen(false);
          setEditorUser(null);
        }}
        onSave={handleEditorSave}
        user={editorUser}
        schedule={editorUser?.work_schedule || []}
        holidayOverrides={editorUser?.holiday_overrides || {}}
        readOnly={!canManageWorkSchedules}
      />

      <div
        id="offcanvas-schedule-exceptions"
        className="uk-modal-full hms-modals uk-modal uk-open uk-dark"
        uk-offcanvas="flip: true; overlay: true"
      >
        <div className="uk-offcanvas-bar">
          <div>
            <button
              className="uk-offcanvas-close uk-close-large"
              type="button"
              data-uk-close
            ></button>
            <div className="uk-modal-header">
              <h2 className="uk-modal-title">Availability Exceptions</h2>
              <div className="uk-text-meta">
                {exceptionsUser
                  ? `${exceptionsUser.first_name || ""} ${exceptionsUser.last_name || ""}`.trim()
                  : ""}
                {!!exceptionsUser?.email ? ` • ${exceptionsUser.email}` : ""}
              </div>

            </div>
          </div>
          <div className="uk-modal-body">

            {exceptionsView === "list" && (
              <>
                <div className="uk-flex uk-flex-between uk-flex-middle uk-margin-small-top uk-margin-bottom">
                  <div className="uk-text-meta">
                    {exceptions.length ? `${exceptions.length} exception(s)` : "No exceptions"}
                  </div>

                  {canManageWorkSchedules && (
                    <button
                      className="download"
                      type="button"
                      onClick={() => {
                        resetForm();
                        setExceptionsView("form");
                      }}
                      uk-tooltip="Add Availability Exception"
                    >
                      <Image src="/images/ops-add.svg" />
                    </button>
                  )}
                </div>

                {exceptions.length === 0 ? (
                  <div className="uk-text-muted uk-padding-small uk-text-center">
                    No exceptions yet.
                  </div>
                ) : (
                  <div className="uk-grid-small" uk-grid="true">
                    {exceptions
                      .slice()
                      .sort((a, b) => String(a.exception_date).localeCompare(String(b.exception_date)))
                      .map((e) => (
                        <div key={e.id} className="uk-width-1-1">
                          <div className="uk-margin-small work-day-row">
                            {deleteException?.id === e.id ? (
                              <div className="uk-flex uk-flex-between uk-flex-middle" style={{ gap: 10 }}>
                                <div className="uk-text-small">
                                  Delete exception for <strong>{moment.utc(e.exception_date).format("MMM DD, YYYY")}</strong>?
                                </div>
                                <div className="uk-flex" style={{ gap: 6 }}>
                                  <button
                                    className="uk-button hms-btn hms-red-btn uk-button-small"
                                    type="button"
                                    onClick={() => confirmDeleteException(e)}
                                  >
                                    Delete
                                  </button>
                                  <button
                                    className="uk-button hms-btn hms-cancel-btn uk-button-small"
                                    type="button"
                                    onClick={() => setDeleteException(null)}
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            ) : (
                            <div className="uk-flex uk-flex-between uk-flex-top" style={{ gap: 10 }}>
                              <div>
                                {/* Date */}
                                <div className="uk-text-bold">
                                  {moment.utc(e.exception_date).format("MM-DD-YYYY ddd")}
                                </div>

                                {/* Type + time */}
                                <div className="uk-text-meta">
                                  {EXCEPTION_TYPE_LABELS[e.exception_type] || "Exception"}
                                  {" • "}
                                  {e.start_time && e.end_time
                                    ? `${moment(e.start_time, "HH:mm:ss").format("h:mm A")}–${moment(
                                        e.end_time,
                                        "HH:mm:ss",
                                      ).format("h:mm A")}`
                                    : "All day"}
                                </div>

                                {/* Reason */}
                                {!!e.reason && (
                                  <div className="uk-margin-small-top">{e.reason}</div>
                                )}

                                {/* Optional note */}
                                {!!e.note && (
                                  <div className="uk-text-meta">{e.note}</div>
                                )}
                              </div>

                              {canManageWorkSchedules && (
                                <div className="uk-flex" style={{ gap: 6 }}>
                                  <button
                                    className="download"
                                    type="button"
                                    onClick={() => {
                                      setEditingException(e);
                                      setForm({
                                        exception_date: e.exception_date ? moment.utc(e.exception_date).format("YYYY-MM-DD") : "",
                                        exception_type: e.exception_type || "off_all_day",
                                        start_time: e.start_time ? String(e.start_time).slice(0, 5) : "",
                                        end_time: e.end_time ? String(e.end_time).slice(0, 5) : "",
                                        reason: e.reason || "",
                                        note: e.note || "",
                                      });
                                      setExceptionsView("form");
                                    }}
                                    uk-tooltip="Edit Availability Exception"
                                  >
                                    <Image src="/images/ops-edit.svg" />
                                  </button>

                                  <button
                                    className="download"
                                    type="button"
                                    onClick={() => setDeleteException(e)}
                                    uk-tooltip="Delete Availability Exception"
                                  >
                                    <Image src="/images/ops-delete.svg" />
                                  </button>
                                </div>
                              )}
                            </div>
                            )}
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </>
            )}

            {/* FORM VIEW */}
            {exceptionsView === "form" && (
              <>
                <div className="uk-margin-small-top uk-margin-small-bottom uk-text-bold">
                  <h4>{editingException ? "Edit Exception" : "Add Exception"}</h4>
                </div>

                <div className="uk-grid-small" uk-grid="true">
                  <div className="uk-width-1-1">
                    <label className="uk-form-label">Date</label>
                    <input
                      className="uk-input"
                      type="date"
                      value={form.exception_date}
                      onChange={(e) => setForm((f) => ({ ...f, exception_date: e.target.value }))}
                    />
                  </div>

                  <div className="uk-width-1-1">
                    <label className="uk-form-label">Type</label>

                    <div>
                      <div
                        className="uk-form-custom"
                        uk-form-custom="target: > * > span:first-child"
                      >
                        <select
                          value={form.exception_type}
                          onChange={(e) => {
                            const newType = e.target.value;
                            setForm((f) => {
                              // Set default times when switching to types that need times
                              if ((newType === "off_partial" || newType === "working_override") && !f.start_time) {
                                return { ...f, exception_type: newType, start_time: "09:00", end_time: "17:00" };
                              }
                              return { ...f, exception_type: newType };
                            });
                          }}
                        >
                          <option value="off_all_day">Off (all day)</option>
                          <option value="off_partial">Partial (works some hours)</option>
                          <option value="working_override">Working (override)</option>
                        </select>

                        <button className="uk-button uk-button-default" type="button" tabIndex={-1}>
                          <span>
                            {form.exception_type === "off_all_day"
                              ? "Off (all day)"
                              : form.exception_type === "off_partial"
                                ? "Partial (works some hours)"
                                : "Working (override)"}
                          </span>
                          <span uk-icon="icon: chevron-down"></span>
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="uk-width-1-1">
                    <label className="uk-form-label">Reason</label>
                    <input
                      className="uk-input"
                      placeholder="Vacation, Appointment…"
                      value={form.reason}
                      onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
                    />
                  </div>

                  {(form.exception_type === "off_partial" || form.exception_type === "working_override") && (
                    <div className="uk-width-1-1">
                      <div className="uk-flex uk-flex-middle" style={{ gap: 12, flexWrap: "wrap" }}>
                        {/* Start */}
                        <div>
                          <label className="uk-form-label uk-margin-small-right">Start</label>
                          <div className="uk-inline">
                            <div className="uk-form-custom" uk-form-custom="target: > * > span:first-child">
                              <select
                                className={classnames({ "uk-form-danger": formErrors.start_time })}
                                value={form.start_time}
                                onChange={(e) => {
                                  setForm((f) => ({ ...f, start_time: e.target.value }));
                                  if (formErrors.start_time) {
                                    setFormErrors((prev) => ({ ...prev, start_time: false }));
                                  }
                                }}
                              >
                                <option value="">-- Select time --</option>
                                {timeOptionsStart.map((t) => (
                                  <option key={`start-${t}`} value={t}>
                                    {hhmmToLabel(t)}
                                  </option>
                                ))}
                              </select>
                              <button className="uk-button uk-button-default" type="button" tabIndex="-1">
                                <span></span>
                                <span uk-icon="icon: chevron-down"></span>
                              </button>
                            </div>
                          </div>
                        </div>

                        {/* End */}
                        <div>
                          <label className="uk-form-label uk-margin-small-right">End</label>
                          <div className="uk-inline">
                            <div className="uk-form-custom" uk-form-custom="target: > * > span:first-child">
                              <select
                                className={classnames({ "uk-form-danger": formErrors.end_time })}
                                value={form.end_time}
                                onChange={(e) => {
                                  setForm((f) => ({ ...f, end_time: e.target.value }));
                                  if (formErrors.end_time) {
                                    setFormErrors((prev) => ({ ...prev, end_time: false }));
                                  }
                                }}
                              >
                                <option value="">-- Select time --</option>
                                {getFilteredEndTimes(form.start_time).map((t) => (
                                  <option key={`end-${t}`} value={t}>
                                    {hhmmToLabel(t)}
                                  </option>
                                ))}
                              </select>
                              <button className="uk-button uk-button-default" type="button" tabIndex="-1">
                                <span></span>
                                <span uk-icon="icon: chevron-down"></span>
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="uk-width-1-1">
                    <label className="uk-form-label">Note</label>
                    <input
                      className="uk-input"
                      placeholder="Optional note…"
                      value={form.note}
                      onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
                    />
                  </div>
                </div>

                {exceptionDateError && (
                  <div className="uk-margin-small" style={{ color: "#f0506e" }}>
                    <span className="uk-text-small">⚠ {exceptionDateError}</span>
                  </div>
                )}

                <div className="uk-flex uk-flex-left uk-margin-top">
                  <button
                    className="uk-button hms-btn hms-red-btn"
                    type="button"
                    disabled={!!exceptionDateError}
                    onClick={async () => {
                      if (!form.exception_date) {
                        UIkit.notification("Please choose a date.", "danger");
                        return;
                      }

                      if (!exceptionsUser?.recipient_id) {
                        UIkit.notification("No recipient selected.", "danger");
                        return;
                      }
                      // Validate time fields for working_override and off_partial
                      const errors = {};
                      if (form.exception_type === "working_override" || form.exception_type === "off_partial") {
                        if (!form.start_time) {
                          errors.start_time = true;
                        }
                        if (!form.end_time) {
                          errors.end_time = true;
                        }
                      }

                      if (Object.keys(errors).length > 0) {
                        setFormErrors(errors);
                        window.UIkit?.notification("Please provide start and end times", { status: "warning" });
                        return;
                      }

                      setFormErrors({});
                      try {
                        const payload = {
                          recipient_id: exceptionsUser.recipient_id,
                          exception_date: form.exception_date,
                          exception_type: form.exception_type,
                          start_time: form.start_time ? `${form.start_time}:00` : null,
                          end_time: form.end_time ? `${form.end_time}:00` : null,
                          reason: form.reason,
                          note: form.note,
                        };

                        if (editingException) {
                          // Update existing exception
                          const response = await workSchedulesCtx.updateException(editingException.id, payload);
                          setExceptions((prev) => prev.map((x) => (x.id === editingException.id ? response : x)));
                        } else {
                          // Create new exception
                          const response = await workSchedulesCtx.createException(payload);
                          setExceptions((prev) => [response, ...prev]);
                        }

                        setExceptionsView("list");
                        resetForm();
                      } catch (error) {
                        console.error("Error saving exception:", error);
                      }
                    }}
                  >
                    {editingException ? "Save" : "Add"}
                  </button>
                  <button
                    className="uk-button hms-btn hms-cancel-btn"
                    type="button"
                    onClick={() => {
                      setExceptionsView("list");
                      resetForm();
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <DeleteDialog
        id="modal-work-schedule-delete"
        title="Delete Work Schedule"
        message={`Are you sure you want to clear the work schedule for ${deleteRecipient?.first_name} ${deleteRecipient?.last_name}`}
        item={deleteRecipient}
        onConfirm={confirmDeleteSchedule}
      />
    </>
  );
};

export default WorkSchedules;